import { useEffect, useState } from "react";
import type { BrandConfig } from "@amsaas/shared";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";
const TENANT_SLUG = import.meta.env.VITE_TENANT_SLUG;

/**
 * Fetches the tenant brand config from GET /api/brand and applies it to :root as
 * CSS custom properties (02-architecture.md §7, 05-frontend-design.md §3.2).
 * All --brand-* references update automatically, branding every component.
 */
export function useTenantBrand(): {
  brand: BrandConfig | null;
  isLoading: boolean;
} {
  const [brand, setBrand] = useState<BrandConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const url = new URL("/api/brand", API_URL);
    // Local dev: the API resolves the tenant by ?slug= when there is no custom
    // domain. In production CloudFront forwards X-Tenant-Slug from the Host.
    if (TENANT_SLUG) url.searchParams.set("slug", TENANT_SLUG);

    fetch(url.toString())
      .then((res) => (res.ok ? (res.json() as Promise<BrandConfig>) : null))
      .then((data) => {
        if (cancelled || !data) {
          setIsLoading(false);
          return;
        }
        applyBrand(data);
        setBrand(data);
        setIsLoading(false);
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { brand, isLoading };
}

const DEFAULT_TITLE = "AMSaaS Portal";

/**
 * Clears provider branding and restores the neutral app defaults (WL-03/04).
 * Used on surfaces that must NEVER carry a provider theme — the marketplace and
 * the neutral login. (App-wide colour tokens are already neutral; globals.css
 * does not bind --brand-* → --primary — see GAP-029 — so this resets the title,
 * favicon, and the --brand-* vars that branded components read.) Runtime swap:
 * entering a neutral surface resets; a provider context re-applies via applyBrand.
 */
export function resetBrand(): void {
  const root = document.documentElement;
  for (const p of [
    "--brand-primary",
    "--brand-secondary",
    "--brand-name",
    "--brand-logo-url",
  ]) {
    root.style.removeProperty(p);
  }
  document.title = DEFAULT_TITLE;
  document.querySelector<HTMLLinkElement>('link[rel="icon"]')?.remove();
}

export function applyBrand(brand: BrandConfig): void {
  const root = document.documentElement;
  root.style.setProperty("--brand-primary", brand.brandPrimaryColour);
  root.style.setProperty("--brand-secondary", brand.brandSecondaryColour);
  root.style.setProperty(
    "--brand-name",
    `'${brand.platformDisplayName.replace(/'/g, "\\'")}'`,
  );
  if (brand.logoUrl) {
    root.style.setProperty("--brand-logo-url", `url('${brand.logoUrl}')`);
  }
  if (brand.platformDisplayName) {
    document.title = brand.platformDisplayName;
  }
  if (brand.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = brand.faviconUrl;
  }
}
